import { Match, Switch, createResource, createSignal, type Component } from 'solid-js';

import { bootState, type Account, type RosterCharacter } from '../net/account';
import { CharacterSelect } from './CharacterSelect';
import { TitleBackdrop, TitleMark } from './TitleArt';
import { LoginScreen } from './LoginScreen';

/**
 * Everything that happens before the game does.
 *
 * ── Why this is not part of the HUD ─────────────────────────────────────────
 *
 * **The Babylon engine is not built until a character is chosen.** That is the last part of
 * the anti-DoS posture in `CLI-USER-MANAGEMENT-SECURE-LOGIN.md`, and the cheapest possible
 * way to honour "no 3D allocation for an unauthenticated client" is for the scene not to
 * exist yet — no engine, no shaders, no textures, no render loop. The gate is plain DOM
 * over a black page.
 *
 * ── Three states, and the third is not a failure ────────────────────────────
 *
 * The boot question is asked of the *server*, because the session is an `HttpOnly` cookie
 * this code cannot read:
 *
 *  1. **Signed in** — straight to the roster. This is what makes "log in once" true.
 *  2. **Not signed in** — the login screen.
 *  3. **Accounts are switched off** (`GRIND_DB=""`) — play immediately, anonymously,
 *     exactly as the game worked before accounts existed. Detected from a 404 rather than a
 *     build flag, so a dev build pointed at a production server cannot disagree with it.
 *
 * A *network* failure is none of the three and is shown as itself. Treating it as "accounts
 * are off" would drop a player into a fresh anonymous character on a server that is holding
 * their real one.
 */
export const Gate: Component<{
  /**
   * Start the game. `characterId` is undefined on a server with no accounts, and the socket
   * connects without naming one.
   */
  onPlay: (characterId?: string) => void;
}> = (props) => {
  const [signedIn, setSignedIn] = createSignal<Account | null>(null);

  // One request answers both questions: whether accounts exist, and who is signed in.
  const [boot] = createResource(async () => {
    const state = await bootState();
    setSignedIn(state.account);
    return state;
  });

  const play = (character: RosterCharacter) => props.onPlay(character.id);

  return (
    /*
      **The painted frame, around whichever screen is showing.**

      The sign-in screen used to be a dark panel on a black page while the title screen
      behind it was fully painted — and it is the *first* thing a player sees. Wrapping the
      whole gate rather than each branch means the backdrop and the wordmark do not reload
      between logging in and picking a character, which would flash the art on every step.
    */
    <div class="gate-frame">
      <TitleBackdrop />
      <div class="gate-frame__mark">
        <TitleMark class="gate-frame__logo" />
      </div>
      <Switch>
      <Match when={boot.loading}>
        <div class="gate" data-testid="gate-loading">
          <p class="gate__lede">Waking the server…</p>
        </div>
      </Match>

      <Match when={boot.error}>
        <div class="gate" data-testid="gate-error">
          <div class="plate plate--riveted gate__panel">
            <h1 class="gate__title">Offline</h1>
            {/* Deliberately not a login screen. A player who retypes a correct password
                against a server that is down learns nothing and blames themselves. */}
            <p class="gate__error" role="alert">
              The server could not be reached. Reload once it is back.
            </p>
          </div>
        </div>
      </Match>

      {/* Accounts off: nothing to sign into, so nothing is shown. Going straight through is
          the whole point — this is the configuration the dev stack and the harness use. */}
      <Match when={boot()?.enabled === false}>
        {(() => {
          queueMicrotask(() => props.onPlay(undefined));
          return (
            <div class="gate" data-testid="gate-anonymous">
              <p class="gate__lede">Starting…</p>
            </div>
          );
        })()}
      </Match>

      <Match when={signedIn()}>
        {(account) => (
          <CharacterSelect
            account={account()}
            onPlay={play}
            onSignedOut={() => setSignedIn(null)}
          />
        )}
      </Match>

      <Match when={!signedIn()}>
        <LoginScreen onSignedIn={setSignedIn} />
      </Match>
      </Switch>
    </div>
  );
};
